import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { school, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ClassDetail } from "../components/school/ClassDetail";
import { GuardianCodeCard } from "../components/school/GuardianCodeCard";

const KINDS = [["school", "School class"], ["homeschool", "Homeschool family"]];

const ClassRow = ({ c, active, onClick }) => (
  <button data-testid="class-item" onClick={onClick} className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${active ? "border-[#D4FF3A]/40 bg-[#D4FF3A]/5" : "border-white/[0.06] hover:bg-white/[0.03]"}`}>
    <p className="text-sm text-white font-medium truncate">{c.name}</p>
    <p className="text-xs font-mono text-slate-500 mt-1 flex items-center gap-2">
      <Users size={12} /> {c.student_count}/{c.seats}
      <span data-testid="class-item-kind" className={c.kind === "homeschool" ? "text-[#FBBF24]" : "text-slate-400"}>· {c.kind === "homeschool" ? "homeschool" : "class"}</span>
      <span className={c.status === "active" ? "text-[#D4FF3A]" : "text-slate-500"}>· {c.status === "active" ? "subscribed" : "not subscribed"}</span>
    </p>
  </button>
);

export default function SchoolPage() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("school");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const refresh = () => school.list().then((list) => {
    setClasses(list);
    setActiveId((id) => id || list[0]?.class_id || null);
  });
  useEffect(() => { refresh().catch(() => {}); school.info().then(setInfo).catch(() => {}); }, []);

  if (user.role === "student" || user.kids_mode) {
    return (
      <div className="what-app h-screen grid place-items-center text-center px-6" data-testid="school-forbidden">
        <div>
          <p className="text-slate-300">{user.kids_mode ? "Turn off Kids Mode to manage school classes." : "Only teachers can manage classes."}</p>
          <Link to="/" className="what-btn-primary mt-6 inline-flex">Back to WHAT?</Link>
        </div>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const c = await school.create(name, kind);
      setName("");
      setActiveId(c.class_id);
      await refresh();
      toast.success(kind === "homeschool" ? "Homeschool family created" : "Class created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="what-app min-h-screen overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/" data-testid="school-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft size={15} /> Back to WHAT?</Link>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white mt-6">School & homeschool</h1>
        <p className="text-slate-400 mt-3 max-w-2xl">
          WHAT? isn't a school learning app. It's for information and fun, so curious kids can ask questions and debate with three AIs.
          Set up a class or a homeschool family, create logins for each child, and give them all WHAT? Pro with one subscription:
          {" "}<span data-testid="school-price-note" className="text-white">R{info?.price ?? 1499}/month per class</span> for up to {info?.seats ?? 35} students, or
          {" "}<span data-testid="homeschool-price-note" className="text-white">R{info?.homeschool?.price ?? 399}/month</span> for a homeschool family of up to {info?.homeschool?.seats ?? 6} children.
        </p>
        <div className="mt-8 max-w-3xl space-y-4">
          {(classes.length === 0 || classes.some((c) => c.kind !== "homeschool")) && <GuardianCodeCard kind="teacher" />}
          {classes.some((c) => c.kind === "homeschool") && <GuardianCodeCard kind="parent" />}
        </div>
        <div className="grid lg:grid-cols-[300px_1fr] gap-8 mt-10">
          <aside className="space-y-3">
            <div className="flex gap-2" role="radiogroup" aria-label="Type">
              {KINDS.map(([k, label]) => (
                <button key={k} type="button" role="radio" aria-checked={kind === k} data-testid={`new-kind-${k}`} onClick={() => setKind(k)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-mono border transition-colors ${kind === k ? "border-[#D4FF3A]/50 bg-[#D4FF3A]/10 text-[#D4FF3A]" : "border-white/[0.08] text-slate-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>
            <form onSubmit={create} className="flex gap-2">
              <input data-testid="new-class-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={kind === "homeschool" ? "e.g. The Naidoo family" : "e.g. Grade 2B"} className="what-input" />
              <button data-testid="create-class-btn" disabled={busy || !name.trim()} className="what-btn-primary !px-3" aria-label="Create class">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              </button>
            </form>
            {classes.length === 0 && <p data-testid="no-classes" className="text-sm text-slate-500 px-1">No classes yet. Create your first one above.</p>}
            {classes.map((c) => <ClassRow key={c.class_id} c={c} active={c.class_id === activeId} onClick={() => setActiveId(c.class_id)} />)}
          </aside>
          {activeId && <ClassDetail key={activeId} classId={activeId} info={info} onChanged={refresh} />}
        </div>
      </div>
    </div>
  );
}
